import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { disposeTooltip } from "@cervval.js/client-html5-core/src/components/disposable";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { TEXT_TRUNCATE } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { AcceptedHtmlElementContent, appendHTMLElementContent, empty, ignoreResizeObserverLoopLimit } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import { anyReallyAny } from "@cervval.js/common/tools";
import fastdom from "fastdom";
import { Tooltip } from "../../bootstrap";


export interface EllipsisOptions<T = anyReallyAny> {

    /** Value in ellipsis */
    value: ValueOrFunction<T>;

    /** Options of tooltip */
    tooltipOptions: Partial<Tooltip.Options>;

    /** Don't show tooltip on mouse over */
    disableTooltip?: boolean;

    /** Format input value to string, if not given, value is converted into string */
    valueFormatter?: (value: T) => string;

    /**
     * Component fixed width
     * Accepts px, rem, percentage, etc...
     */
    width?: string;

    /**
     * Tooltip is disabled if ellipsis (...) isn't displayed, with this
     * option enable, tooltip is always displayed 
     */
    alwaysEnableTooltip?: boolean;
}

const defaultEllipsisOptions: EllipsisOptions = {

    /** Options of tooltip */
    tooltipOptions: {
        delay: 200
    },

    /** Value in ellipsis */
    value: ""
};

/**
 * Wrap a value into 
 */
@CustomElement({
    name: "cjs-ellipsis",
    elementCssClasses: [TEXT_TRUNCATE]
})
export class CjsEllipsis<T = anyReallyAny> extends WebComponent {

    @TypedWebComponentOptions(defaultEllipsisOptions)
    public readonly options!: WebComponentOptions<EllipsisOptions<T>>;

    /** Tooltip attached to component */
    public tooltip?: Tooltip;
    public tooltipEnabled = true;

    /** String currently displayed inside tooltip */
    public displayedValue: AcceptedHtmlElementContent = "";

    /** true if ellipsis was hovered by user, don't create tooltip if user has never hovered ellipsis */
    public wasHovered = false;

    /** when hovering a  ResizeObserver is installed */
    private _resizeObserver?: ResizeObserver;

    constructor(value: ValueOrFunction<T>, options?: Partial<EllipsisOptions<T>>, customOptions?: OverrideCustomElementConfig) {
        super(customOptions);
        this.options.setWithoutRefresh({
            ...options,
            value
        });
    }

    /** Init component */
    public override init(): void {

        // Add mouseenter event listener, don't create Boostrap tooltip if user hasn't hovered ellipsis
        this.addEventListener("mouseenter", () => {
            this.wasHovered = true;

            // Create tooltip
            this._refreshTooltip();
            // Observe for component resizes to display or not tooltip
            this._resizeObserver = new ResizeObserver(entries => {
                ignoreResizeObserverLoopLimit(entries, () => { this._refreshTooltip(); });
            });
            this._resizeObserver.observe(this);

            // Manually show tooltip, during first mouse hover, tooltip wasn't displayed, it wasn't instantiated
            if (this.tooltip) {
                this.tooltip.show();
            }
        }, { once: true });
    }

    /** When element is added to DOM */
    public connectedCallback(): void {
        this._refreshTooltip();
        this._resizeObserver?.observe(this);
    }

    /** When element is removed from DOM */
    public disconnectedCallback(): void {
        this._resizeObserver?.disconnect();
        disposeTooltip(this);
    }

    /** On component refresh */
    public override refresh(): void {
        this._refreshWidth();
        this._refreshValue();
        if (this.wasHovered) { this._refreshTooltip(); }
    }

    /** Refresh width of ellipsis */
    private _refreshWidth(): void {
        const ellipsisWidth = this.style.width;
        const optionsWidth = this.options.width;
        // Refresh width of component
        if (optionsWidth != null && optionsWidth !== "" && ellipsisWidth !== optionsWidth) {
            this.style.width = optionsWidth;
        }
    }

    /** Refresh value displayed in ellipsis */
    private _refreshValue(): void {

        // Retrieve value to display, accepts string or htmlElement
        const value = getVOFValue(this.options.value);
        if (value != null) {
            const valueFromFormatter = this.options.valueFormatter?.(value);

            // Check value is retrieve from formatter
            if (valueFromFormatter != null) { this.displayedValue = valueFromFormatter; }
            else {// Assign displayed value
                if (value instanceof HTMLElement) {
                    this.displayedValue = value;
                } else {
                    this.displayedValue = value ? String(value) : "";
                }
            }
        } else {
            this.displayedValue = "";
        }

        // Add value into component
        empty(this);
        appendHTMLElementContent(this, this.displayedValue);
    }

    /** Refresh tooltip of CjsEllipsis */
    private _refreshTooltip(): void {
        // Useless to create tooltip if user have never hovered text
        if (!this.wasHovered) { return; }

        fastdom.measure(() => {

            // Check if component has no scroll <=> is currently not in displaying ellipsis (...)
            // Don't add tooltip if all content is visible
            // this.clientWidth and this.scrollWidth can recalculate style and layout so we test it last for performance reason
            const noTooltip = this.options.disableTooltip || !Boolean(this.options.alwaysEnableTooltip) && this.scrollWidth <= this.clientWidth;

            // Check if tooltip should be displayed, else disable it
            if (noTooltip) { this.disableTooltip(); return; }

            // If tooltip is already defined, useless to re-recreate it
            if (this.tooltip) {
                this.enableTooltip();
                return;// TODO Check 'displayedValue' hasn't changed before return, else update value displayed by tooltip
            }

            // Retrieve value to display
            let valueToDisplayInTooltip = this.displayedValue;

            /** Enable html if displayed value is an htmlElement */
            if (this.displayedValue instanceof HTMLElement) {
                this.options.setWithoutRefresh({
                    tooltipOptions: {
                        ...this.options.tooltipOptions,
                        html: true
                    }
                });
                valueToDisplayInTooltip = this.displayedValue.cloneNode(true) as HTMLElement;
            }

            // Add tooltip if needed
            this.tooltip = new Tooltip(this, {
                title: valueToDisplayInTooltip,
                ...this.options.tooltipOptions
            });
        });
    }

    /** Enable tooltip of ellipsis if defined, or not already enabled */
    public enableTooltip(): void {
        if (!this.tooltip || this.tooltipEnabled) { return; }
        this.tooltip.enable();
        this.tooltipEnabled = true;
    }

    /** Disable tooltip of ellipsis if defined, or not already disabled */
    public disableTooltip(): void {
        if (!this.tooltip || !this.tooltipEnabled) { return; }
        this.tooltip.disable();
        this.tooltipEnabled = false;
    }
}
